import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import { renderPageTitleV2 } from "@components/MetaTags/helpers";
import MetaTags from "@components/MetaTags/MetaTags";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useScrollTo } from "@hooks/useScrollTo";
import ThriveNavigation from "@pages/Section/layouts/ThrivePage/components/ThriveNavigation";
import ThriveNewsletter from "@pages/Section/layouts/ThrivePage/components/ThriveNewsLetter";
import ThriveSocialMediaBanner from "@pages/Section/layouts/ThrivePage/components/ThriveSocialMediaBanner";
import ThriveTopBanner from "@pages/Section/layouts/ThrivePage/components/ThriveTopBanner";
import { thriveEventsPageMetaTags } from "@pages/Thrive/constants";
import ThriveFavicon from "@src/app/components/Favicon/ThriveFavicon";
import { sectionNavigationItems } from "@util/constant";
import { useDetectAdBlock } from "adblock-detect-react";

import ThriveBanner from "./components/ThriveBanner";
import ThrivePastEvents from "./components/ThrivePastEvents";

export default function ThriveEventsPage(): React.ReactElement {
  const { breadcrumbsRef, isShowBreadCrumb } = useBreadCrumbState();
  const adBlockDetected = useDetectAdBlock();

  const [scrollToRef, setShouldScrollTo] = useScrollTo<HTMLDivElement>();

  const thriveMetatags = thriveEventsPageMetaTags;
  const title = renderPageTitleV2({
    kind: "custom",
    title:
      "Events, thrive - The Business Times | Career, Money and Life for Gen Zs in Singapore",
  });

  return (
    <div className="w-full" data-testid="thrive-events-page">
      <ThriveFavicon />

      <AdSettings
        adNames={["lb1", "imu2", "prestitial", "catfish"]}
        path={RouteFactory.thriveAboutUs}
      />

      <MetaTags
        title={title}
        ogSiteName={title}
        description={thriveMetatags.description}
        ogDescription={thriveMetatags.description}
        ogImageAlt={thriveMetatags.description}
        ogImageUrl={thriveMetatags.ogImageUrl}
        slug={RouteFactory.thriveEvents}
      />

      <GAData
        title="Thrive Events"
        level2="miscellaneous"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[50px] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[250px]"
      />

      <div ref={breadcrumbsRef}></div>

      <Header
        displayBreadcrumb={isShowBreadCrumb}
        parentCategory={RouteFactory.thrive.replace("/", "")}
        childCategory={RouteFactory.thriveEvents.replace("/", "")}
        categoryDataMapping={sectionNavigationItems}
      />

      <Container>
        <ThriveTopBanner />

        <div className="mt-4 flex flex-col-reverse gap-6 lg:mt-0 lg:mt-8 lg:flex-row">
          <div className="w-full lg:w-8/12">
            <ThriveNavigation />

            <ThriveBanner setShouldScrollTo={setShouldScrollTo} />

            <ThrivePastEvents scrollToRef={scrollToRef} />
          </div>

          <div className="border-b border-green-100 lg:mb-6 lg:mt-18 lg:block lg:w-4/12 lg:border-b-0">
            <ThriveSocialMediaBanner />

            <ThriveNewsletter />

            <div className="mb-6 mt-2 hidden lg:block lg:h-[800px]">
              <Ad
                adType="imu2"
                className="flex flex-col items-center justify-center lg:!sticky lg:top-18"
              />
            </div>
          </div>
        </div>
      </Container>

      <PulseEntry />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
}
